/**
 * Tour steps for the admin/dispatcher onboarding.
 *
 * Two tours kept deliberately short (<= 2 min, action-oriented):
 *  - `desktopSteps` → full guidance (≈ 8 steps) when screen width ≥ 768
 *  - `mobileSteps`  → condensed (≈ 5 steps) focused on menu + core actions
 *
 * Each step targets an existing `data-testid` on the AdminDashboard so we
 * never create fragile custom markers. Elements we can't always rely on
 * (e.g. first ride row when the list is empty) are guarded inside
 * `useAdminOnboarding.js`.
 */

const base = {
  popoverClass: "sdrive-onboarding-popover",
  showButtons: ["next", "previous", "close"],
  // French labels override driver.js defaults (English by default).
  nextBtnText: "Suivant →",
  prevBtnText: "← Précédent",
  doneBtnText: "Terminer",
};

const WELCOME = {
  popover: {
    title: "Bienvenue sur votre dispatcher",
    description:
      "<p>2 minutes pour maîtriser l'essentiel. Vous pouvez quitter à tout moment — le guide est relançable via le bouton <b>?</b> en haut à droite.</p>",
    ...base,
  },
};

const FINAL = {
  popover: {
    title: "Vous êtes prêt",
    description:
      "<p>Vous pouvez relancer ce guide à tout moment via le bouton <b>?</b> en haut à droite. Bon dispatch&nbsp;!</p>",
    ...base,
  },
};

/* ─────────────────────────── DESKTOP (≥ 768 px) ─────────────────────── */
export const desktopSteps = [
  WELCOME,
  {
    element: '[data-testid="create-ride-btn"]',
    popover: {
      title: "1. Créer une course",
      description:
        "<p>Le point de départ de tout&nbsp;: renseignez le départ, l'arrivée, la date, le prix. Astuce&nbsp;: vous pouvez aussi coller un texte ou une capture (IA) pour remplir automatiquement.</p>",
      side: "bottom",
      align: "start",
      ...base,
    },
  },
  {
    element: '[data-testid="manage-drivers-btn"]',
    popover: {
      title: "2. Gérer vos chauffeurs",
      description:
        "<p>Ajoutez vos chauffeurs, validez leur profil pro (VTC, assurance) puis assignez-leur des courses. Les chauffeurs ont leur propre espace mobile.</p>",
      side: "bottom",
      align: "end",
      ...base,
    },
  },
  {
    element: '[data-onboarding="ride-row"], [data-testid="admin-mobile-cards"]',
    popover: {
      title: "3. Comprendre les statuts",
      description:
        "<p>Chaque course passe par&nbsp;:</p>" +
        "<ul style='margin:6px 0 0 14px;padding:0;line-height:1.6'>" +
        "<li>🟢 <b>Disponible</b> — publiée aux chauffeurs</li>" +
        "<li>🟠 <b>Intéressé</b> — un chauffeur a postulé</li>" +
        "<li>🔵 <b>Attribuée</b> — chauffeur désigné, ER&nbsp;/&nbsp;Sur place&nbsp;/&nbsp;PEC&nbsp;/&nbsp;Terminée</li>" +
        "<li>⚫ <b>Terminée</b> — course livrée</li>" +
        "</ul>",
      side: "top",
      align: "start",
      ...base,
    },
  },
  {
    popover: {
      title: "4. Passager vs client facturé",
      description:
        "<p>Deux notions à ne pas confondre&nbsp;:</p>" +
        "<p>👤 <b>Passager</b> — la personne qui monte dans la voiture (nom utilisé pour l'accueil, WhatsApp).</p>" +
        "<p>💳 <b>Client facturé</b> — qui paie la course (agence, client direct, entreprise). Ce sont parfois 2 personnes différentes&nbsp;: une agence réserve pour son client final.</p>",
      ...base,
    },
  },
  {
    popover: {
      title: "5. Transfert vs Mise à disposition",
      description:
        "<p>🚗 <b>Transfert</b> — trajet ponctuel, point A → point B (ex&nbsp;: CDG → Paris).</p>" +
        "<p>⏱️ <b>Mise à disposition</b> — chauffeur réservé à la durée (ex&nbsp;: 4h dans Paris, shopping, rendez-vous).</p>" +
        "<p class='sdrive-hint'>Le service_type est indépendant du mode de paiement ↓.</p>",
      ...base,
    },
  },
  {
    popover: {
      title: "6. PAB vs Standard",
      description:
        "<p>Mode de règlement — indépendant du type de service&nbsp;:</p>" +
        "<p>💵 <b>PAB</b> (Paiement À Bord) — le client règle le chauffeur directement à la fin de la course.</p>" +
        "<p>🧾 <b>Standard</b> — le client paie la société (donneur d'ordre), vous réglez ensuite le chauffeur via <i>Paiements</i>.</p>",
      ...base,
    },
  },
  {
    element: '[data-testid="notifications-bell"]',
    popover: {
      title: "7. Temps réel",
      description:
        "<p>Chaque action chauffeur (intérêt, ER, Sur place, PEC, Terminée) remonte ici instantanément. Vous pouvez activer les notifications push pour ne rien manquer.</p>",
      side: "bottom",
      align: "end",
      ...base,
    },
  },
  FINAL,
];

/* ─────────────────────────── MOBILE (< 768 px) ─────────────────────── */
export const mobileSteps = [
  WELCOME,
  {
    element: '[data-testid="create-ride-btn"]',
    popover: {
      title: "Créer une course",
      description:
        "<p>Le bouton principal&nbsp;: départ, arrivée, date, prix. L'IA peut pré-remplir depuis un texte ou une capture.</p>",
      side: "bottom",
      align: "start",
      ...base,
    },
  },
  {
    element: '[data-onboarding="ride-row"], [data-testid="admin-mobile-cards"]',
    popover: {
      title: "Statuts d'une course",
      description:
        "<p>🟢 Disponible · 🟠 Intéressé · 🔵 Attribuée · ⚫ Terminée. Tapez sur une carte pour voir les détails et agir.</p>",
      side: "top",
      align: "start",
      ...base,
    },
  },
  {
    popover: {
      title: "2 distinctions clés",
      description:
        "<p>👤 <b>Passager</b> = qui monte. 💳 <b>Client facturé</b> = qui paie.</p>" +
        "<p>🚗 <b>Transfert</b> = A→B. ⏱️ <b>Mise à disposition</b> = réservé à la durée.</p>" +
        "<p>💵 <b>PAB</b> = payé à bord. 🧾 <b>Standard</b> = payé à la société.</p>",
      ...base,
    },
  },
  {
    element: '[data-testid="notifications-bell"]',
    popover: {
      title: "Notifications temps réel",
      description:
        "<p>Les actions de vos chauffeurs remontent ici (ER, Sur place, Terminée). Activez le push pour être notifié même hors de l'app.</p>",
      side: "bottom",
      align: "end",
      ...base,
    },
  },
  FINAL,
];
